import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { serializeForServer } from "~/lib/server-serialization";

interface PartnershipData {
  slug: string;
  name: string;
  shortName: string | null;
  logoUrl: string | null;
  welcomeMessage: string | null;
  bonusCredits: number;
}

const fetchPartnership = createServerFn({ method: "GET" })
  .validator(z.string().min(1).max(64))
  .handler(async ({ data: slug }): Promise<PartnershipData | null> => {
    const { eq, and, createDb } = await import("@alchema/db");
    const schema = await import("@alchema/db");
    const url = process.env.DATABASE_URL;
    if (!url || url.includes("user:pass@")) return null;
    const db = createDb(url);
    const [partnership] = await db
      .select({
        slug: schema.universityPartnerships.slug,
        name: schema.universityPartnerships.name,
        shortName: schema.universityPartnerships.shortName,
        logoUrl: schema.universityPartnerships.logoUrl,
        welcomeMessage: schema.universityPartnerships.welcomeMessage,
        bonusCredits: schema.universityPartnerships.bonusCredits,
      })
      .from(schema.universityPartnerships)
      .where(
        and(
          eq(schema.universityPartnerships.slug, slug),
          eq(schema.universityPartnerships.isActive, true),
        ),
      );
    return serializeForServer(partnership ?? null);
  });

export const Route = createFileRoute("/uni/$slug")({
  loader: ({ params }) => fetchPartnership({ data: params.slug }),
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData
          ? `${loaderData.name} x Alchema — Free AI Career Tools`
          : "University Partner — Alchema",
      },
      {
        name: "description",
        content: loaderData
          ? `Sign up for Alchema via ${loaderData.name} and get ${loaderData.bonusCredits} free credits for AI resume tailoring, ATS scoring, and interview prep.`
          : "Join Alchema through your university and get free AI career tools.",
      },
    ],
  }),
  component: UniSlugPage,
});

const CHECK = (
  <svg className="h-4 w-4 flex-shrink-0" style={{ color: "var(--accent)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

function UniSlugPage() {
  const partnership = Route.useLoaderData();
  const { slug } = Route.useParams();

  if (!partnership) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "var(--bg)" }}>
        <div className="text-center">
          <h1 className="text-2xl font-bold" style={{ color: "var(--text)" }}>
            University Not Found
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            We couldn't find a partnership for "{slug}".
          </p>
          <Link
            to="/university"
            className="mt-6 inline-block rounded-[10px] px-6 py-2.5 text-sm font-medium text-white"
            style={{ backgroundColor: "var(--accent)" }}
          >
            Learn About Partnerships
          </Link>
        </div>
      </div>
    );
  }

  const welcomeMsg =
    partnership.welcomeMessage ||
    `Welcome, ${partnership.name} students! Get started with Alchema to supercharge your job applications with AI.`;

  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ backgroundColor: "var(--bg)" }}>
      <div className="w-full max-w-lg animate-fade-up">
        {/* Header */}
        <div className="text-center">
          {partnership.logoUrl && (
            <img
              src={partnership.logoUrl}
              alt={`${partnership.name} logo`}
              className="mx-auto mb-4 h-12 w-auto"
            />
          )}
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
            Welcome, {partnership.shortName || partnership.name} students!
          </h1>
          <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {welcomeMsg}
          </p>
        </div>

        {/* Bonus Credits Callout */}
        <div
          className="mt-6 rounded-[13px] border p-4 text-center"
          style={{ borderColor: "var(--accent)", backgroundColor: "rgba(38, 109, 240, 0.05)" }}
        >
          <p className="text-sm font-medium" style={{ color: "var(--accent)" }}>
            You get {partnership.bonusCredits} free credits to start
          </p>
          <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
            Use them for resume tailoring, ATS scoring, and more
          </p>
        </div>

        {/* Signup CTA */}
        <div className="mt-6 text-center">
          <Link
            to="/auth/signup"
            search={{ ref: `uni-${partnership.slug}` }}
            className="inline-block w-full rounded-[10px] px-6 py-3 text-center text-sm font-medium text-white hover:opacity-90"
            style={{ backgroundColor: "var(--accent)" }}
          >
            Create Free Account
          </Link>
          <p className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>
            Already have an account?{" "}
            <Link to="/auth/login" className="underline">Sign in</Link>
          </p>
        </div>

        {/* What You Get */}
        <div
          className="mt-8 rounded-[13px] border border-[var(--border)] p-6"
          style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
        >
          <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
            What you get
          </h3>
          <ul className="mt-3 space-y-2.5">
            {[
              "AI resume tailoring for every job application",
              "ATS scoring to beat applicant tracking systems",
              "Cover letter generation matched to job descriptions",
              "Interview prep with role-specific questions",
              "Salary data from official DACH sources",
            ].map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                {CHECK}
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs" style={{ color: "var(--text-muted)" }}>
          GDPR-compliant. EU-native AI. No US data transfers.{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
